import { useEffect, useRef, useState } from "react";
import { Col, Dropdown, DropdownButton, Form, FormControl, Placeholder, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstConfirm from "../../components/DstConfirm/DstConfirm";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { LoadedPermissionsData, Role } from "./Permissions.d";
import {
  closeRolePopup,
  closeRoutePopup,
  createNewRole,
  duplicateRole,
  fetchPermissionsData,
  fetchRoleUsers,
  getPermissionsWithGodMode,
  handleDeleteRole,
  handleSelectAllPermissions,
  handleUnselectAllPermissions,
  openRolePopup,
  openRoutePopup,
  searchUserRole,
  updateRole,
} from "./Permissions.functions";
import "./Permissions.scss";
import AddUsersToRolePopup from "./components/AddUsersToRolePopup/AddUsersToRolePopup";
import { rolePopup, routePopup } from "./components/Popups";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const ALL_RIGHTS_KEY = "*_/**";

const _handlePermissionSelect = (
  roleId: string,
  route: string,
  method: string,
  rolePermissions: { [roleId: string]: string[] },
  setRolePermissions: Function
) => {
  let currentPermissions = rolePermissions[roleId] || [];
  const permissionKey = `${method}_${route}`;

  if (
    (permissionKey !== ALL_RIGHTS_KEY && currentPermissions.includes(ALL_RIGHTS_KEY)) ||
    permissionKey === ALL_RIGHTS_KEY
  ) {
    currentPermissions = [];
  }
  setRolePermissions({
    ...rolePermissions,
    [roleId]: currentPermissions.includes(permissionKey)
      ? currentPermissions.filter((permission) => permission !== permissionKey)
      : [...currentPermissions, permissionKey],
  });
};

const Permissions = () => {
  const { language, showAlert, popAlert } = useSession();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<any | null>();
  const [roles, setRoles] = useState<{ id: string; description: string; users?: number[] }[]>([]);
  const [permissions, setPermissions] = useState<{ [route: string]: any }>({});
  const [tags, setTags] = useState([]);
  const [showPopups, setShowPopups] = useState<{ [key: string]: number | string } | null>(null);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [roleUsers, setRoleUsers] = useState<{ [roleId: string]: { id: number; full_name: string }[] }>({});
  const [loaded, setLoaded] = useState<LoadedPermissionsData>({
    roles: false,
    permissions: false,
    tags: false,
    rolesUsers: {},
  });
  const [displayPage, setDisplayPage] = useState(0);
  const [rolePermissions, setRolePermissions] = useState<{ [roleId: string]: string[] }>({});
  const [openAddUsersToRoleModal, setOpenAddUsersToRoleModal] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedRoleId = searchParams.get("role");
  const [searchValue, setSearchValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const _fetchPermissionsData = () =>
    fetchPermissionsData(setRoles, setRolePermissions, setPermissions, setTags, setSelectedRole, setLoaded);

  useEffect(() => {
    _fetchPermissionsData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (preselectedRoleId && loaded.roles) {
      const foundRole = roles.find((role) => String(role.id) === preselectedRoleId);
      if (foundRole) {
        setSelectedRole(foundRole);
      }
    }
  }, [preselectedRoleId, roles, loaded.roles]);

  useEffect(() => {
    setDisplayPage(0);
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole) {
      fetchRoleUsers(String(selectedRole.id), setRoleUsers, setLoaded, displayPage);
    }
  }, [selectedRole, displayPage]);

  const filteredPermissions = Object.entries(permissions).filter(([route, data]) => {
    const tagsMatch = selectedTags.every((tag) =>
      data
        .map((dataMethod: any) => dataMethod?.documentation?.tags)
        .flat()
        .includes(tag)
    );
    const methodsMatch =
      selectedMethods.length === 0 ||
      selectedMethods.some((method) => data.map((dataMethod: any) => dataMethod.method).includes(method));
    return tagsMatch && methodsMatch;
  });
  const handleUserRoleSearch = () => {
    searchUserRole(
      searchValue,
      roles,
      (role: Role) => {
        setSelectedRole(role);
        navigate(`/permissions?role=${role.id}`);
        setSearchValue("");
      },
      setIsSearching,
      showAlert!,
      popAlert!,
      language
    );
  };

  return (
    <div id="permissions">
      <div className="d-sm-flex justify-content-between px-3 my-4 align-items-center">
        <h1 className="mb-3 mb-sm-0">{translate(language, "PAGES.PERMISSIONS.TITLE")}</h1>
        <div className="d-flex align-items-center gap-2 px-sm-3">
          <FormControl
            id="search-user-role"
            type="search"
            placeholder={translate(language, "PAGES.PERMISSIONS.SEARCH.PLACEHOLDER")}
            className="shadow-sm border rounded s-regular"
            value={searchValue}
            ref={inputRef}
            onChange={(event) => setSearchValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleUserRoleSearch();
            }}
          />
          <DstButton
            variant="primary"
            btnImageBefore={<span className="dst-icon-search-md s-regular" />}
            value=""
            btnClass="px-3"
            clickFunction={handleUserRoleSearch}
            loading={isSearching}
            disabled={isSearching || !searchValue.trim()}
          />
        </div>
      </div>
      <div className="border-top">
        <Row className="mx-0">
          <Col sm={10} xl={2} className="px-0 pt-4 roles-selector border-end d-flex flex-column">
            <h2 className="px-3 pb-3">{translate(language, "PAGES.PERMISSIONS.TAB_TITLE")}</h2>
            <div className="flex-grow-1 overflow-auto">
              {loaded.roles ? (
                roles.map((role: any) => (
                  <div
                    key={role.id}
                    className={`border-bottom p-2 px-md-3 clickable role-selecter ${
                      selectedRole && role.id === selectedRole.id ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedRole(role);
                      navigate(`/permissions?role=${role.id}`);
                    }}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <div>
                        <span className="dst-icon-users-01 me-2" />
                        {role.name}
                      </div>
                      <div className="align-content-center">
                        <span className="dst-icon-chevron-right" />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Placeholder as="div" animation="glow" role="mockup">
                  {[...Array(10)].map((_, index) => (
                    <div key={`mock-role-selector-${index}`} className="border-bottom p-2 px-md-3 role-selecter">
                      <div className="d-flex w-100 justify-content-between">
                        <Placeholder xs={10} role="mockup" />
                      </div>
                    </div>
                  ))}
                </Placeholder>
              )}
            </div>

            <div className="mt-auto p-3">
              <DstButton
                value={translate(language, "PAGES.PERMISSIONS.CREATE_ROLE_BUTTON_TITLE")}
                variant="light"
                backgroundColor="var(--color-medium-gray)"
                color="var(--color-black)"
                btnClass="w-100"
                btnImageBefore={<span className="dst-icon-plus-circle me-2" />}
                clickFunction={() => createNewRole(language, _fetchPermissionsData)}
              />
            </div>
          </Col>
          <Col>
            {selectedRole ? (
              <div className="permissions-tab">
                <Row className="permissions-panel">
                  <Col xl={9} className="pt-4 h-100">
                    <Row className="permissions-panel-header">
                      <Col xl={4}>
                        <h2>
                          {selectedRole.name}
                          {translate(language, "PAGES.PERMISSIONS.TABS.TITLE")}
                        </h2>
                        <div className="d-flex gap-2 mb-2">
                          <MultiFilterDropdown
                            language={language}
                            tags={tags}
                            onSelectTags={(selectedItems: Array<any>) => {
                              setSelectedTags(selectedItems);
                            }}
                            mock={!loaded.tags}
                            initialStateText={translate(
                              language,
                              "PAGES.PERMISSIONS.MULTI_FILTER_DROPDOWN.INITIAL_TEXT"
                            )}
                            inputTitle={translate(language, "PAGES.PERMISSIONS.MULTI_FILTER_DROPDOWN.TITLE")}
                            inputLabel={translate(language, "PAGES.PERMISSIONS.MULTI_FILTER_DROPDOWN.INPUT_LABEL")}
                            inputPlaceholder={translate(
                              language,
                              "PAGES.PERMISSIONS.MULTI_FILTER_DROPDOWN.INPUT_PLACEHOLDER"
                            )}
                            showSearchBar
                          />
                          <MultiFilterDropdown
                            tags={METHODS}
                            onSelectTags={(selectedItems: (string | number)[]) =>
                              setSelectedMethods(selectedItems as string[])
                            }
                            mock={!loaded.permissions}
                            inputTitle={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.SELECT_METHODS")}
                            language={language}
                          />
                        </div>
                      </Col>
                      <Col className="text-end">
                        <div>
                          <DstButton
                            backgroundColor="var(--color-off-white)"
                            color="var(--color-black)"
                            variant="light"
                            value={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.SAVE_ROLE")}
                            btnImageBefore={<span className="dst-icon-save-01 me-2" />}
                            clickFunction={() =>
                              updateRole(
                                language,
                                selectedRole.id,
                                rolePermissions[String(selectedRole.id)],
                                _fetchPermissionsData
                              )
                            }
                          />
                          <DstButton
                            backgroundColor="var(--color-off-white)"
                            color="var(--color-black)"
                            variant="light"
                            value={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.DUPLICATE_ROLE")}
                            btnImageBefore={<span className="dst-icon-copy-07 me-2" />}
                            btnClass="ms-2"
                            clickFunction={() => duplicateRole(language, selectedRole, _fetchPermissionsData)}
                          />
                          <DstButton
                            backgroundColor="var(--color-off-white)"
                            color="var(--color-black)"
                            variant="light"
                            value={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.RIGHTS_SUMMARY")}
                            btnImageBefore={<span className="dst-icon-file-02 me-2" />}
                            btnClass="ms-2"
                            clickFunction={() => openRolePopup(setShowPopups, String(selectedRole.id))}
                          />
                          <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/roles/*" }]}>
                            <DstButton
                              value={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.DELETE_ROLE")}
                              variant="danger"
                              btnClass="ms-2"
                              btnImageBefore={<span className="dst-icon-trash-01 me-2" />}
                              clickFunction={() => setShowDeleteConfirm(true)}
                              disabled={!selectedRole}
                            />
                          </PermissionComponent>
                        </div>
                        <div className="d-flex justify-content-end">
                          <DropdownButton
                            id="dropdown-check-method"
                            title={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.CHOOSE_ALL")}
                            variant="light"
                            className="s-bold mt-2 ms-2"
                          >
                            {METHODS.map((method) => (
                              <Dropdown.Item
                                key={method}
                                onClick={() =>
                                  handleSelectAllPermissions(
                                    method,
                                    rolePermissions,
                                    selectedRole.id,
                                    permissions,
                                    selectedTags,
                                    setRolePermissions
                                  )
                                }
                              >
                                {method}
                              </Dropdown.Item>
                            ))}
                          </DropdownButton>
                          <DropdownButton
                            id="dropdown-check-method"
                            title={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.UNCHOOSE_ALL")}
                            variant="light"
                            className="s-bold mt-2 ms-2"
                          >
                            {METHODS.map((method) => (
                              <Dropdown.Item
                                key={method}
                                onClick={() =>
                                  handleUnselectAllPermissions(
                                    method,
                                    rolePermissions,
                                    selectedRole.id,
                                    permissions,
                                    selectedTags,
                                    setRolePermissions
                                  )
                                }
                              >
                                {method}
                              </Dropdown.Item>
                            ))}
                          </DropdownButton>
                        </div>
                      </Col>
                    </Row>
                    {loaded.permissions ? (
                      <div className="permissions-list overflow-auto mt-3">
                        <div className="d-flex my-3 justify-content-between">
                          <div className="d-flex">
                            <DstButton
                              variant="outline-primary"
                              value=""
                              btnImageAfter={<span className="dst-icon-info-circle" />}
                              btnClass="d-flex fw-bolder me-3 px-2"
                              clickFunction={() => openRoutePopup(setShowPopups, "/**")}
                            />
                            <div className="red-bright-font tinted-red-background rounded border px-2">{"/**"}</div>
                          </div>
                          <Form className="d-flex">
                            <Form.Group controlId={`formBasicCheckbox_god_mode`} className="d-flex">
                              <Form.Check
                                type="checkbox"
                                label="All"
                                id={`role-${selectedRole.id}-checkbox-god-mode`}
                                className="w-fit me-3 align-self-center checkbox-style"
                                onChange={() =>
                                  _handlePermissionSelect(
                                    selectedRole.id,
                                    "/**",
                                    "*",
                                    rolePermissions,
                                    setRolePermissions
                                  )
                                }
                                checked={rolePermissions[String(selectedRole.id)]?.includes(ALL_RIGHTS_KEY)}
                              />
                            </Form.Group>
                          </Form>
                        </div>
                        {filteredPermissions.map(([route, data]) => (
                          <div key={route} className="d-flex my-3 justify-content-between">
                            <div className="d-flex">
                              <DstButton
                                value=""
                                variant="outline-primary"
                                btnImageAfter={<span className="dst-icon-info-circle" />}
                                btnClass="d-flex fw-bolder me-3 px-2"
                                clickFunction={() => openRoutePopup(setShowPopups, route)}
                              />
                              <div className="red-bright-font tinted-red-background rounded border px-2 text-nowrap me-3">
                                {route}
                              </div>
                            </div>
                            <Form className="d-flex">
                              <Form.Group controlId={`formBasicCheckbox_${route}`} className="d-flex">
                                {METHODS.map((method: any) => (
                                  <Form.Check
                                    key={`${route}_${method}`}
                                    type="checkbox"
                                    label={method}
                                    id={`role-${selectedRole.id}-${method}-checkbox-${route}`}
                                    className="w-fit me-3 align-self-center checkbox-style"
                                    disabled={!data.map((elem: any) => elem.method).includes(method)}
                                    onChange={() =>
                                      _handlePermissionSelect(
                                        selectedRole.id,
                                        route,
                                        method,
                                        rolePermissions,
                                        setRolePermissions
                                      )
                                    }
                                    checked={rolePermissions[String(selectedRole.id)]?.includes(`${method}_${route}`)}
                                  />
                                ))}
                              </Form.Group>
                            </Form>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Placeholder animation="glow">
                        {[...Array(10)].map((_, index: number) => (
                          <Placeholder key={`mock-permission-line-${index}`} className="mt-3" xs={12} role="mockup" />
                        ))}
                      </Placeholder>
                    )}
                  </Col>
                  <Col className="pt-4 border-start min-h-100vh">
                    <div className="d-flex justify-content-between align-items-center">
                      <h2 className="mb-0">{translate(language, "PAGES.PERMISSIONS.TABS.USER_COLUMN.TITLE")}</h2>
                      <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/users/roles/batch" }]}>
                        <DstButton
                          btnClass="xxs-bold d-flex align-items-center"
                          btnImageBefore={<span className="dst-icon-users-plus d-flex m-regular" />}
                          tooltip={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.ADD_NEW_USER")}
                          clickFunction={() => setOpenAddUsersToRoleModal(true)}
                          value=""
                        />
                      </PermissionComponent>
                    </div>
                    {loaded.rolesUsers?.[selectedRole.id] ? (
                      <ul className="list-unstyled ps-0 mt-3">
                        {roleUsers[selectedRole.id]?.length > 0 ? (
                          roleUsers[selectedRole.id]?.map((user) => (
                            <li
                              key={user.id}
                              className="medium-gray-background list-style-none w-fit border my-2 px-2 rounded clickable"
                              onClick={(event) => completeNavigate(event, navigate, `/user/${user.id}`)}
                            >
                              {user.full_name}
                            </li>
                          ))
                        ) : (
                          <p className="my-2 smoke-gray-font m-regular">
                            {translate(language, "PAGES.PERMISSIONS.TABS.USER_COLUMN.NO_USER")}
                          </p>
                        )}
                      </ul>
                    ) : (
                      <Placeholder as="div" animation="glow" role="mockup">
                        <Placeholder xs={10} role="mockup" />
                        <Placeholder xs={10} role="mockup" />
                        <Placeholder xs={10} role="mockup" />
                      </Placeholder>
                    )}
                    {loaded.rolesUsers?.[selectedRole.id] &&
                      displayPage < 5 &&
                      roleUsers[selectedRole.id].length === (1 + displayPage) * 10 && (
                        <div>
                          <DstButton
                            value={translate(language, "PAGES.PERMISSIONS.TABS.BUTTONS.LOAD_MORE")}
                            clickFunction={() => setDisplayPage((displayPage: number) => displayPage + 1)}
                            variant="secondary"
                            btnClass="mt-2 s-regular"
                          />
                        </div>
                      )}
                    {rolePopup(language, showPopups, () => closeRolePopup(setShowPopups), selectedRole)}
                  </Col>
                </Row>
              </div>
            ) : (
              <p className="text-center silver-gray-font mt-3">{translate(language, "PAGES.PERMISSIONS.NO_ROLE")}</p>
            )}
          </Col>
        </Row>
        {routePopup(language, showPopups, () => closeRoutePopup(setShowPopups), getPermissionsWithGodMode(permissions))}
      </div>
      <AddUsersToRolePopup
        displayPage={displayPage}
        language={language}
        openAddUsersModal={openAddUsersToRoleModal}
        roleId={selectedRole?.id}
        setRoleUsers={setRoleUsers}
        setOpenAddUsersModal={setOpenAddUsersToRoleModal}
        setLoaded={setLoaded}
      />
      <DstConfirm
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={() =>
          selectedRole &&
          handleDeleteRole(language, selectedRole.id, () => {
            setShowDeleteConfirm(false);
            _fetchPermissionsData();
          })
        }
        title={translate(language, "PAGES.PERMISSIONS.DELETE_ROLE_CONFIRM.TITLE")}
        messageConfirm={translate(language, "PAGES.PERMISSIONS.DELETE_ROLE_CONFIRM.MESSAGE").replace(
          "{{ROLE_NAME}}",
          selectedRole?.name
        )}
        confirmTextButton={translate(language, "PAGES.PERMISSIONS.DELETE_ROLE_CONFIRM.BUTTON_CONFIRM")}
        icon="dst-icon-trash-01"
        iconColor="red"
        iconBackgroundColor="var(--color-light-pink)"
      />
    </div>
  );
};

export default Permissions;
